import { useEffect, useState } from 'react';
import { zipSync } from 'fflate';
import { getArchivedPdf } from '@/api/client';
import type { StatementListItem } from './types';
import s from './StatementBatchExport.module.css';

const MAX_COUNT = 20;
const MAX_TOTAL = 100 * 1024 * 1024;

/** 仅打包老版已生成的正式归档；不重新生成或修改对账单。 */
export function StatementBatchExport({ items, onClose }: { items: StatementListItem[]; onClose: () => void }) {
  const available = items.filter((item) => item.status === 'LOCKED' && item.pdfFileUrl);
  const missing = items.filter((item) => item.status === 'LOCKED' && !item.pdfFileUrl).length;
  const [selected, setSelected] = useState<string[]>(() => available.slice(0, MAX_COUNT).map((item) => item.id));
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [busy, onClose]);

  const toggle = (id: string) => setSelected((current) => current.includes(id) ? current.filter((value) => value !== id) : current.length < MAX_COUNT ? [...current, id] : current);
  const download = async () => {
    if (!selected.length || busy) return;
    setBusy(true); setError('');
    try {
      const files: Record<string, Uint8Array<ArrayBuffer>> = {};
      let total = 0;
      const chosen = available.filter((item) => selected.includes(item.id));
      for (const [index, item] of chosen.entries()) {
        setProgress(`正在读取 ${index + 1} / ${chosen.length}：${item.statementNo}`);
        const bytes = await getArchivedPdf(item.pdfFileUrl!);
        total += bytes.byteLength;
        if (total > MAX_TOTAL) throw new Error('所选 PDF 合计超过 100 MB，请分批导出。');
        const safeNo = item.statementNo.replace(/[^\w\u4e00-\u9fff-]/g, '_');
        files[`${safeNo || `statement_${index + 1}`}.pdf`] = bytes;
      }
      setProgress('正在打包 PDF…');
      const archive = zipSync(files, { level: 0 });
      const blob = new Blob([archive as Uint8Array<ArrayBuffer>], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `对账单PDF_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '导出失败，请重试。');
    } finally { setBusy(false); setProgress(''); }
  };

  return <div className={s.scrim} onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <section className={s.dialog} role="dialog" aria-modal="true" aria-label="批量导出对账单 PDF">
      <header><h2>批量导出 PDF</h2><button type="button" onClick={onClose} disabled={busy} aria-label="关闭">×</button></header>
      <p className={s.hint}>按当前筛选结果导出已锁账且已有正式 PDF 的对账单。单次最多 {MAX_COUNT} 份，合计不超过 100 MB。</p>
      {missing > 0 && <p className={s.notice}>{missing} 份已锁账对账单尚未生成 PDF，请先在老版生成。</p>}
      {!available.length ? <p className={s.empty}>当前筛选结果中没有可导出的正式 PDF。</p> : <>
        <div className={s.list}>
          {available.map((item) => <label key={item.id} className={s.row}>
            <input type="checkbox" checked={selected.includes(item.id)} disabled={busy || (!selected.includes(item.id) && selected.length >= MAX_COUNT)} onChange={() => toggle(item.id)} />
            <span><b>{item.statementNo}</b><small>{item.partner?.name || '—'} · {item.settlementMonth}</small></span>
          </label>)}
        </div>
        <p className={s.count}>已选 {selected.length} / {available.length} 份</p>
      </>}
      {progress && <p className={s.progress} role="status">{progress}</p>}
      {error && <p className={s.error} role="alert">{error}</p>}
      <footer><button type="button" onClick={onClose} disabled={busy}>取消</button><button type="button" className={s.primary} disabled={!selected.length || busy} onClick={() => void download()}>{busy ? '导出中…' : '下载 ZIP'}</button></footer>
    </section>
  </div>;
}
